import React, { useEffect, useState } from "react";
import { Order } from "../../__generated__/graphql";
import { motion } from "framer-motion";

interface OrderStatusBarProps {
  order: Order;
}

const OrderStatusBar = ({ order }: OrderStatusBarProps) => {
  // 현재 시간 상태 관리 (1분마다 업데이트) - Hooks는 항상 최상단에
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000); // 1분마다 업데이트

    return () => clearInterval(timer);
  }, []);

  // 예약팩(packId 존재)이 아니거나 취소된 주문이면 렌더링하지 않음 (취소된 경우 별도 UI가 있거나 흐름이 끊기므로 제외)
  // 픽업 종료 시간(packPickupEndTime)이 지났다면 렌더링하지 않음 (지난 주문은 상태 바 불필요)
  if (!order.packId || order.isCanceled) return null;

  if (order.packPickupEndTime) {
    const pickupEnd = new Date(order.packPickupEndTime);
    // 현재 시간이 픽업 종료 시간보다 이후라면 렌더링 안 함
    if (now > pickupEnd) return null;
  }

  // 상태 계산
  const isConfirmed = order.isPackConfirmed;
  const isPickedUp = order.isPickupCompleted;

  // 현재 진행 단계 (0: 주문완료, 1: 예약확정, 2: 픽업시작, 3: 픽업완료)
  // 픽업완료가 가장 마지막 단계
  let currentStep = 0;
  if (isPickedUp) {
    currentStep = 3; // 픽업완료
  } else if (isConfirmed) {
    currentStep = 1; // 기본 예약확정

    // 예약확정 상태이고, 현재 시간이 픽업 시작 시간을 지났으면 '픽업시작' 단계로
    if (order.packPickupStartTime) {
      const pickupStart = new Date(order.packPickupStartTime);
      if (now >= pickupStart) {
        currentStep = 2; // 픽업시작
      }
    }
  }

  // steps 재정의 (active 상태 업데이트)
  const steps = [
    { label: "주문완료", active: true },
    { label: "예약확정", active: currentStep >= 1 },
    { label: "픽업시작", active: currentStep >= 2 },
    { label: "픽업완료", active: currentStep >= 3 },
  ];

  // 게이지 바 너비 계산 (0%, 33%, 66%, 100%)
  const progressWidth = `${(currentStep / 3) * 100}%`;

  return (
    <div className="bg-white p-5 pt-12 pb-4 mb-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="relative mx-4">
        {/* 회색 배경 라인 */}
        <div className="absolute top-1/2 left-0 w-full h-1.5 bg-gray-100 -translate-y-1/2 rounded-full" />

        {/* 진행 상태 라인 (Primary Color) */}
        <div
          className="absolute top-1/2 left-0 h-1.5 bg-primary -translate-y-1/2 rounded-full transition-all duration-500 ease-in-out"
          style={{ width: progressWidth }}
        />

        {/* 단계별 원 및 텍스트 */}
        <div className="relative flex justify-between items-center w-full">
          {steps.map((step, index) => {
            const isCurrentStep = index === currentStep;

            return (
              <div key={index} className="flex flex-col items-center relative">
                {/* 라벨 텍스트 - 원 위로 이동 */}
                <div
                  className={`absolute -top-8 w-20 text-center text-xs font-bold transition-colors duration-300 ${
                    step.active ? "text-primary" : "text-gray-400"
                  }`}
                >
                  {step.label}
                </div>

                {/* 원 (Circle) */}
                <motion.div
                  className={`w-4 h-4 rounded-full border-[3px] z-10 box-content transition-colors duration-300 ${
                    step.active
                      ? "bg-primary border-primary"
                      : "bg-gray-100 border-gray-100"
                  }`}
                  animate={
                    isCurrentStep ? { scale: [1, 1.1, 1] } : { scale: 1 }
                  }
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* 1. 예약확정 시간 (예약확정 전일 때 표시) */}
                {index === 1 &&
                  !isConfirmed &&
                  !isPickedUp &&
                  order.packAutoConfirmTime && (
                    <div className="absolute top-6 w-24 text-center text-[10px] font-medium text-gray-500">
                      {new Date(order.packAutoConfirmTime).toLocaleTimeString(
                        "ko-KR",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }
                      )}
                    </div>
                  )}

                {/* 2. 픽업시작 시간 (픽업시작 전일 때 표시 - 예약확정 상태에서도 표시 가능) */}
                {index === 2 && !isPickedUp && order.packPickupStartTime && (
                  <div className="absolute top-6 w-24 text-center text-[10px] font-medium text-gray-500">
                    {new Date(order.packPickupStartTime).toLocaleTimeString(
                      "ko-KR",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 현재 상태에 따른 안내 문구 */}
      <div className="mt-8 text-center">
        {isPickedUp ? (
          <>
            <p className="text-[15px] font-semibold text-gray-800 break-keep">
              픽업이 완료되었습니다
            </p>
            <p className="text-[15px] font-semibold text-gray-800 break-keep">
              맛있게 드세요! 😋
            </p>
          </>
        ) : isConfirmed ? (
          currentStep === 2 ? ( // 픽업시작 단계일 때
            <>
              <p className="text-[15px] font-semibold text-gray-800 break-keep">
                픽업 시간이 되었습니다!
              </p>
              <p className="text-[15px] font-semibold text-gray-800 break-keep">
                매장에 방문하여 픽업해주세요 🏃
              </p>
            </>
          ) : (
            // 예약확정 단계일 때
            <>
              <p className="text-[15px] font-semibold text-gray-800 break-keep">
                {new Date(order.packPickupStartTime).toLocaleTimeString(
                  "ko-KR",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }
                )}
                ~
                {new Date(order.packPickupEndTime).toLocaleTimeString("ko-KR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </p>
              <p className="text-[15px] font-semibold text-gray-800 break-keep">
                픽업 시간에 맞춰 픽업해요! 👋
              </p>
            </>
          )
        ) : (
          <p className="text-[15px] font-semibold text-gray-800 break-keep">
            📝 주문이 접수되었습니다
          </p>
        )}
      </div>
    </div>
  );
};

export default OrderStatusBar;
